import createClient, { ClientOptions } from 'openapi-fetch';
import { paths } from './codegen/swagger-types';
import { canUseWindow } from './utils';
import {
  userAgentMiddleware,
  UserAgentMiddlewareOptions,
} from '@repo/openapi-fetch-user-agent-middleware';
import {
  PricingResponseDTOBuilder,
  CalculatePriceResponseDTOBuilder,
  PricingOptionsResponseDTOBuilder,
} from './builders/pricing-dto.builder';
import { MarketingContentResponseDTOBuilder } from './builders/marketing-content-dto.builder';
import {
  DeliveryRecipeResponseDTOBuilder,
  DeliveryResponseDTOBuilder,
  MyDeliveriesResponseDTOBuilder,
} from './builders/delivery-dto.builder';
import { SubscriptionOptionsDTOBuilder } from './builders/subscription-options-dto.builder';
import { SignupRequestDTOBuilder } from './builders/signup-dto.builders';
import { DeliveryOptionsDTOBuilder } from './builders/delivery-options-dto.builder';
import { SignupMarketingConsentOptionsDTOBuilder } from './builders/marketing-consent-options-dto.builder';
import {
  MenuResponseDTOBuilder,
  MenuResponseRecipeDTOBuilder,
  RecipeIngredientDTOBuilder,
  NutritionalInformationDTOBuilder,
} from './builders/menu-dto.builders';
import { MenuCollectionDietaryCollectionsBuilder } from './builders/menu-dietary-collection-dto.builder';

export { default as experienceApiSpec } from './openapi/experience-api-swagger.json';

export type ExperienceApiClientOptions = ClientOptions & {
  userAgent?: UserAgentMiddlewareOptions;
};

export type ExperienceApiClient = ReturnType<typeof createBaseExperienceApiClient>;

function createBaseExperienceApiClient(clientOptions?: ExperienceApiClientOptions) {
  const { userAgent, ...restOptions } = clientOptions ?? {};

  const client = createClient<paths>({
    ...restOptions,
    credentials: 'include',
  });

  if (userAgent) {
    client.use(userAgentMiddleware(userAgent));
  }

  return client;
}

export default createBaseExperienceApiClient;

/**
 * Modified fetch - follows server-side redirects for ALL request methods
 * This is required to redirect unauthenticated client-side requests to /login
 * This is not the default behaviour - POST/PUT/PATCH do not follow redirects by default
 */
async function browserOnlyFetch(input: Request): Promise<Response> {
  const response = await fetch(input);

  // Only force browser requests to follow redirects
  if (response.redirected && canUseWindow()) {
    window.location.href = response.url;
  }

  return response;
}

/**
 * Returns a singleton experience API client for browser use only
 * Ensures ALL browser requests are proxied via the webapp's auth middleware
 */
export function $createBrowserExperienceApiClient() {
  let browserExperienceApiClient: ExperienceApiClient | null = null;

  return function getBrowserExperienceApiClient(
    clientOptions?: Omit<ExperienceApiClientOptions, 'baseUrl' | 'fetch'>,
  ) {
    if (browserExperienceApiClient) {
      return browserExperienceApiClient;
    }

    browserExperienceApiClient = createBaseExperienceApiClient({
      ...clientOptions,
      fetch: browserOnlyFetch,
      baseUrl: '/proxy', // Browser requests to experience API are always proxied
    });

    return browserExperienceApiClient;
  };
}

export const getBrowserExperienceApiClient = $createBrowserExperienceApiClient();

export function extractExperienceApiData<T, U>(fetchResponse: {
  data?: { data: T };
  error?: U;
  response: Response;
}) {
  const { data, error, response } = fetchResponse;
  if (error || data === undefined) {
    throw new Error(`Error calling ${response.url}; status ${response.status}`);
  }
  return data.data;
}

export {
  PricingResponseDTOBuilder,
  DeliveryRecipeResponseDTOBuilder,
  DeliveryResponseDTOBuilder,
  MyDeliveriesResponseDTOBuilder,
  CalculatePriceResponseDTOBuilder,
  MarketingContentResponseDTOBuilder,
  PricingOptionsResponseDTOBuilder,
  SignupRequestDTOBuilder,
  MenuResponseDTOBuilder,
  MenuResponseRecipeDTOBuilder,
  RecipeIngredientDTOBuilder,
  NutritionalInformationDTOBuilder,
  MenuCollectionDietaryCollectionsBuilder,
  SubscriptionOptionsDTOBuilder,
  DeliveryOptionsDTOBuilder,
  SignupMarketingConsentOptionsDTOBuilder,
};

export type {
  ExperienceApiTypes,
  ExperienceAPIOperation,
  SignupDTOTypes,
  DeliveryDTOTypes,
  PricingDTOTypes,
  MenuDTOTypes,
  RecipeDTOTypes,
  DeliveryOptionsDTOTypes,
  MarketingConsentOptionsDTOTypes,
  SubscriptionOptionsDTOTypes,
  SubscriptionDTOTypes,
  MarketingContentDTOTypes,
  ReactivationLandingDTOTypes,
} from './types';
